'use client'

import { motion, useInView } from 'framer-motion'
import { Users, Award, Target, Lightbulb, TrendingUp, Globe2 } from 'lucide-react'
import DynamicCompanyInfo from './DynamicCompanyInfo'
import { useCompanyInfo } from '@/contexts/CompanyInfoContext'
import { useRef, useEffect, useState } from 'react'

function AnimatedCounter({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0)
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })

  useEffect(() => {
    if (!isInView) return
    let start = 0
    const duration = 2000
    const increment = target / (duration / 16)
    const timer = setInterval(() => {
      start += increment
      if (start >= target) {
        setCount(target)
        clearInterval(timer)
      } else {
        setCount(Math.floor(start))
      }
    }, 16)
    return () => clearInterval(timer)
  }, [isInView, target])

  return <span ref={ref}>{count}{suffix}</span>
}

export default function About() {
  const { companyInfo } = useCompanyInfo()

  const establishedYear = companyInfo?.businessInfo?.establishedYear
  const currentYear = new Date().getFullYear()
  const yearsInBusiness = establishedYear && establishedYear <= currentYear ? currentYear - establishedYear : null
  const officeCount = companyInfo?.additionalInfo?.officeLocations?.filter(Boolean).length || 0
  const certCount = companyInfo?.additionalInfo?.certifications?.filter(Boolean).length || 0

  // Build stats only from real data. parsEmployeeCount handles "10-50" ranges.
  const parseEmployeeCount = (v?: string): number | null => {
    if (!v) return null
    const nums = (v.match(/\d+/g) || []).map(Number)
    return nums.length ? Math.max(...nums) : null
  }
  const employeeMax = parseEmployeeCount(companyInfo?.additionalInfo?.employeeCount)

  const stats: { value: number; suffix: string; label: string; icon: React.ReactNode }[] = []
  if (yearsInBusiness !== null && yearsInBusiness > 0) stats.push({ value: yearsInBusiness, suffix: '+', label: 'Years in Business', icon: <TrendingUp className="w-5 h-5" /> })
  if (employeeMax) stats.push({ value: employeeMax, suffix: '+', label: 'Team Members', icon: <Users className="w-5 h-5" /> })
  if (officeCount) stats.push({ value: officeCount, suffix: '', label: officeCount === 1 ? 'Office' : 'Offices', icon: <Globe2 className="w-5 h-5" /> })
  if (certCount) stats.push({ value: certCount, suffix: '', label: 'Certifications', icon: <Award className="w-5 h-5" /> })

  const values = [
    { icon: <Target className="w-6 h-6" />, title: 'Mission Driven', description: 'Empowering businesses with innovative technology solutions.' },
    { icon: <Lightbulb className="w-6 h-6" />, title: 'Innovation First', description: 'Staying ahead of tech trends for cutting-edge solutions.' },
    { icon: <Users className="w-6 h-6" />, title: 'Client Focused', description: 'Building lasting partnerships through exceptional service.' },
    { icon: <Award className="w-6 h-6" />, title: 'Quality Assured', description: 'Highest standards in every project we deliver.' }
  ]

  return (
    <section id="about" className="py-20 sm:py-28 bg-gradient-to-b from-sky-50/50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-sky-100/20 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left: Stats & Visual */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            {/* Stats Grid — only when real data exists */}
            {stats.length > 0 && (
            <div className="grid grid-cols-2 gap-4 mb-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl p-5 shadow-sm border border-sky-100/50 hover:shadow-lg hover:shadow-sky-100/50 transition-all duration-300 group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-lg bg-sky-50 text-sky-500 group-hover:bg-sky-500 group-hover:text-white transition-all duration-300">
                      {stat.icon}
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-slate-900">
                    <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
                </motion.div>
              ))}
            </div>
            )}

            {/* Decorative illustration */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="relative h-48 bg-gradient-to-br from-sky-100/50 to-blue-100/50 rounded-2xl overflow-hidden flex items-center justify-center"
            >
              <div className="absolute inset-0">
                <motion.div animate={{ x: [0, 20, 0], y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 6, ease: 'easeInOut' }} className="absolute top-6 left-10 w-16 h-16 bg-sky-200/60 rounded-full" />
                <motion.div animate={{ x: [0, -15, 0], y: [0, 15, 0] }} transition={{ repeat: Infinity, duration: 8, ease: 'easeInOut' }} className="absolute bottom-8 right-12 w-20 h-20 bg-blue-200/50 rounded-full" />
                <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ repeat: Infinity, duration: 4, ease: 'easeInOut' }} className="absolute top-12 right-20 w-12 h-12 bg-violet-200/40 rounded-xl rotate-12" />
              </div>
              <div className="relative z-10 text-center">
                <p className="text-2xl font-bold text-slate-700">🚀 Growing Together</p>
                <p className="text-sm text-slate-500 mt-1">Innovation meets excellence</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right: Content */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-100 text-sky-600 text-sm font-medium mb-4"
              >
                <Award className="w-4 h-4" />
                Who We Are
              </motion.div>
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
                About <span className="gradient-text">
                  <DynamicCompanyInfo type="companyName" />
                </span>
              </h2>
              <p className="text-slate-500 leading-relaxed">
                {companyInfo?.businessInfo?.description
                  ? companyInfo.businessInfo.description
                  : `${companyInfo?.companyName || 'We'}${establishedYear ? `, established in ${establishedYear},` : ''} ${companyInfo?.companyName ? 'helps' : 'help'} businesses leverage technology to achieve their goals — combining technical expertise with a focus on quality, reliability, and long-term partnership.`}
              </p>
            </div>

            {/* Values Grid */}
            <div className="grid sm:grid-cols-2 gap-4">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  viewport={{ once: true }}
                  className="p-4 border border-sky-100 rounded-xl hover:shadow-md hover:shadow-sky-50 hover:border-sky-200 transition-all duration-300 group bg-white/80"
                >
                  <div className="text-sky-500 mb-2 group-hover:scale-110 transition-transform duration-300">{value.icon}</div>
                  <h3 className="font-semibold text-slate-900 mb-1 text-sm">{value.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
